import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  ShieldAlert,
  Download,
  Maximize2,
  CheckCircle2,
  XCircle,
  DollarSign,
  Loader2,
  ImageIcon,
} from "lucide-react";

const STATUS_PILL = {
  submitted: { bg: "bg-sky-100", fg: "text-sky-700", label: "received" },
  under_review: { bg: "bg-amber-100", fg: "text-amber-700", label: "under review" },
  approved: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "approved" },
  rejected: { bg: "bg-rose-100", fg: "text-rose-700", label: "rejected" },
  paid: { bg: "bg-violet-100", fg: "text-violet-700", label: "paid" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

export default function SubmissionDetailDrawer({ submissionId, onClose, onChanged }) {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [txnId, setTxnId] = useState("");
  const [finalPayout, setFinalPayout] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/submissions/${submissionId}`);
      setSub(r.data);
      setFinalPayout(r.data.final_payout ?? r.data.estimated_payout ?? "");
      setAdminNotes(r.data.admin_notes || "");
    } catch (e) {
      toast.error("Could not load submission");
    }
  }, [submissionId]);

  useEffect(() => {
    if (submissionId) load();
  }, [submissionId, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!submissionId) return null;

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/submissions/${submissionId}/status`, {
        status,
        admin_notes: adminNotes,
        final_payout: Number(finalPayout) || null,
      });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    if (!txnId.trim()) return toast.error("Enter transaction ID");
    setBusy(true);
    try {
      await api.post(`/admin/submissions/${submissionId}/mark-paid`, {
        transaction_id: txnId.trim(),
        final_payout: Number(finalPayout) || 0,
        note: adminNotes,
      });
      toast.success("Marked as paid");
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const pill = sub ? STATUS_PILL[sub.status] || STATUS_PILL.submitted : null;
  const riskScore = sub ? Math.min(95, (sub.fraud_flags?.length || 0) * 18) : 0;
  const riskLabel =
    riskScore < 35 ? { text: "LOW", cls: "bg-emerald-100 text-emerald-700" }
    : riskScore < 65 ? { text: "MEDIUM", cls: "bg-amber-100 text-amber-700" }
    : { text: "HIGH", cls: "bg-rose-100 text-rose-700" };

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-end"
      data-testid="admin-detail-drawer"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        data-testid="admin-detail-backdrop"
      />
      {/* panel */}
      <aside className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-white px-6 py-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Submission Details</h2>
          <button
            onClick={onClose}
            data-testid="admin-detail-close"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {!sub ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading submission…
          </div>
        ) : (
          <>
            <div className="px-6 py-5 space-y-6 flex-1">
              {/* Reference + status */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                    Reference Number
                  </div>
                  <div className="mt-1 text-2xl font-mono font-bold text-sky-600 tracking-wider">
                    {sub.reference}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.fg}`}
                  data-testid="admin-detail-status"
                >
                  {pill.label}
                </span>
              </div>

              {/* Customer grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <Field icon={User} label="Customer" value={sub.full_name} bold />
                <Field icon={Mail} label="Email" value={<a href={`mailto:${sub.email}`} className="text-sky-600 hover:underline">{sub.email}</a>} />
                <Field icon={Phone} label="Phone" value={sub.phone || "—"} bold />
                <Field icon={Calendar} label="Submitted" value={fmtDate(sub.created_at)} bold />
              </div>

              {/* Gift cards */}
              <section>
                <div className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                  <CreditCard className="w-4 h-4" />
                  <span>Gift Cards</span>
                </div>
                <div className="space-y-4">
                  {sub.cards.map((c, idx) => (
                    <CardBlock key={idx} index={idx + 1} card={c} />
                  ))}
                </div>
              </section>

              {/* Total Value */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">Total Value</div>
                <div className="text-2xl font-bold text-emerald-700 font-mono">
                  ${Number(sub.final_payout ?? sub.estimated_payout ?? 0).toFixed(2)}
                </div>
              </div>

              {/* Payment Method */}
              <section>
                <div className="flex items-center gap-2 text-slate-700 font-semibold mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span>Payment Method</span>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                  <div className="text-base font-semibold text-slate-900 capitalize">
                    {sub.payout_method}
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5 font-mono break-all">
                    {sub.payout_handle}
                  </div>
                </div>
              </section>

              {/* Fraud / Risk */}
              <section>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Fraud Score</div>
                    <div className="mt-1 text-3xl font-bold text-slate-900 font-mono">{riskScore}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Risk Level</div>
                    <span className={`mt-1 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${riskLabel.cls}`}>
                      {riskLabel.text}
                    </span>
                  </div>
                </div>
                {sub.fraud_flags?.length > 0 && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
                    <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span><strong>Flags:</strong> {sub.fraud_flags.join(", ")}</span>
                  </div>
                )}
              </section>

              {/* Admin notes + final payout (compact) */}
              <section className="space-y-3">
                <label className="block">
                  <span className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                    Admin notes (internal)
                  </span>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={2}
                    data-testid="admin-detail-notes"
                    className="w-full p-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Final payout</span>
                    <input
                      type="number"
                      value={finalPayout}
                      onChange={(e) => setFinalPayout(e.target.value)}
                      data-testid="admin-detail-final-payout"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Transaction ID</span>
                    <input
                      value={txnId}
                      onChange={(e) => setTxnId(e.target.value)}
                      data-testid="admin-detail-txn"
                      placeholder="for Mark Paid"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </label>
                </div>
              </section>
            </div>

            {/* Sticky bottom actions */}
            <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 grid grid-cols-3 gap-3">
              <button
                onClick={() => updateStatus("approved")}
                disabled={busy}
                data-testid="admin-detail-approve"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
              <button
                onClick={() => updateStatus("rejected")}
                disabled={busy}
                data-testid="admin-detail-reject"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-semibold transition disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
              <button
                onClick={markPaid}
                disabled={busy}
                data-testid="admin-detail-mark-paid"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold transition disabled:opacity-50"
              >
                <DollarSign className="w-4 h-4" /> Mark Paid
              </button>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}

function Field({ icon: Icon, label, value, bold }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-400 font-semibold">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-slate-900 ${bold ? "font-semibold" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function CardBlock({ index, card }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
        <div className="font-semibold text-slate-900">
          Card {index}: {card.brand_name}{" "}
          <span className="text-slate-500 font-normal">({card.card_type || "—"})</span>
        </div>
        <div className="text-emerald-700 font-bold font-mono">
          ${Number(card.balance).toFixed(0)}
        </div>
      </div>

      {/* Sensitive info */}
      <div className="m-4 border-2 border-rose-300 rounded-xl p-4 bg-rose-50/40">
        <div className="flex items-center gap-1.5 text-rose-700 text-[11px] font-bold tracking-wider uppercase mb-3">
          <ShieldAlert className="w-3.5 h-3.5" /> Sensitive Card Information
        </div>
        <Row label="Card Number">
          <code className="block w-full px-3 py-2 bg-white border border-rose-200 rounded-lg text-base font-mono font-bold text-slate-900 break-all">
            {card.card_number || card.card_number_masked || "—"}
          </code>
        </Row>
        <Row label="PIN / CVV" tight>
          <code className="block w-full px-3 py-2 bg-white border border-rose-200 rounded-lg text-base font-mono font-bold text-slate-900 break-all">
            {card.pin || "—"}
          </code>
        </Row>
        <Row label="Expiry" tight>
          <code className="block w-full px-3 py-2 bg-white border border-rose-200 rounded-lg text-base font-mono font-bold text-slate-900 break-all">
            {card.expiry_date || "—"}
          </code>
        </Row>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-700">
          <span><span className="text-slate-500">Condition:</span> <strong className="capitalize">{card.condition || "—"}</strong></span>
          <span><span className="text-slate-500">Receipt:</span> <strong>{card.has_receipt ? "Yes" : "No"}</strong></span>
          {typeof card.rate === "number" && (
            <span><span className="text-slate-500">Rate:</span> <strong>{card.rate}%</strong></span>
          )}
          {typeof card.estimated_payout === "number" && (
            <span><span className="text-slate-500">Payout:</span> <strong className="text-emerald-700">${card.estimated_payout.toFixed(2)}</strong></span>
          )}
        </div>
      </div>

      {/* Images */}
      <CardImages card={card} />
    </div>
  );
}

function Row({ label, children, tight }) {
  return (
    <div className={tight ? "mt-2" : ""}>
      <div className="text-xs text-slate-500 mb-1">{label}:</div>
      {children}
    </div>
  );
}

function CardImages({ card }) {
  const slots = [
    { key: "front", label: "Card Front", path: card.images?.front },
    { key: "back", label: "Card Back", path: card.images?.back },
    { key: "receipt", label: "Receipt", path: card.images?.receipt },
  ].filter((s) => s.path);

  if (slots.length === 0) {
    return (
      <div className="mx-4 mb-4 px-4 py-6 border border-dashed border-slate-200 rounded-xl text-center text-sm text-slate-400">
        No images uploaded for this card.
      </div>
    );
  }

  return (
    <div className="mx-4 mb-4">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Card Images</div>
      <div className="space-y-4">
        {slots.map((s) => (
          <ImageCell key={s.key} label={s.label} path={s.path} />
        ))}
      </div>
    </div>
  );
}

function ImageCell({ label, path }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let url = null;
    const token = localStorage.getItem("gf_admin_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("fetch");
        return r.blob();
      })
      .then((b) => {
        url = URL.createObjectURL(b);
        setSrc(url);
      })
      .catch(() => setErr(true));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  const download = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    const ext = path.split(".").pop() || "jpg";
    a.download = `${label.toLowerCase().replace(/\s+/g, "-")}-${path.split("/").pop()?.split(".")[0] || "image"}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 text-sm font-semibold text-slate-900">{label}</div>
      <div className="bg-white px-4 pt-4 pb-2 flex items-center justify-center min-h-[180px]">
        {err ? (
          <div className="flex flex-col items-center text-slate-400 text-sm py-6">
            <ImageIcon className="w-8 h-8 mb-1" />
            <span>Failed to load image</span>
          </div>
        ) : !src ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <img src={src} alt={label} className="max-h-72 max-w-full object-contain rounded-lg" />
        )}
      </div>
      {src && !err && (
        <div className="grid grid-cols-2 border-t border-slate-200">
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 py-3 text-sky-600 hover:bg-slate-50 text-sm font-semibold border-r border-slate-200"
            data-testid={`admin-img-fullsize-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Maximize2 className="w-4 h-4" /> View Full Size
          </a>
          <button
            type="button"
            onClick={download}
            className="inline-flex items-center justify-center gap-2 py-3 text-emerald-600 hover:bg-slate-50 text-sm font-semibold"
            data-testid={`admin-img-download-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Download className="w-4 h-4" /> Download
          </button>
        </div>
      )}
    </div>
  );
}
